import { JsonSchemaArray } from '@evidentid/json-schema/interfaces/JsonSchema';
import { isArray } from '@evidentid/json-schema/schemaChecks';
import {
    Criterion, CriterionField,
    CriterionInput,
    CriterionTemplate,
} from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

import {
    EditableRequirementCriterion,
} from '@/modules/decisioning-criteria/models/EditableRequirements.model';
import { castYesNoTextToBoolean } from '@/modules/decisioning-criteria/utils/castYesNoTextToBoolean';
import { findCriterionTemplate } from '@/modules/decisioning-criteria/utils/riskProfileCriteria';

function castValueToArray(value: any, templateRef: CriterionField): any {
    if (!isArray(templateRef?.schema) || Array.isArray(value)) {
        return value;
    }
    const expectedItemType = ((templateRef.schema as JsonSchemaArray<any>).items).type;
    if (value == null) {
        return [];
    }
    if (typeof value === expectedItemType) {
        return [ value ];
    }
    return value;
}

/**
 * Casts a raw reference value to match the template schema type.
 * Wraps scalars into arrays when the template expects an array, and reads Yes/No text as the
 * boolean a Yes/No criterion declares.
 */
export function castValueToMatchSchema(value: any, templateRef: CriterionField): any {
    return castValueToArray(castYesNoTextToBoolean(value, templateRef?.schema), templateRef);
}

function shouldCastToArray(
    criterionReference: CriterionField,
    templateReference: CriterionField,
): boolean {
    if (!isArray(templateReference.schema)) {
        return false;
    }
    if (Array.isArray(criterionReference.value)) {
        return false;
    }
    const expectedItemType = ((templateReference.schema as JsonSchemaArray<any>).items).type;
    const criterionSchema = criterionReference.schema;
    if (criterionSchema != null) {
        return (criterionSchema as any).type === expectedItemType;
    }
    return typeof criterionReference.value === expectedItemType;
}

function castToArray(
    criterionReference: CriterionField,
    templateReference: CriterionField,
): CriterionField {
    return shouldCastToArray(criterionReference, templateReference)
        ? {
            ...criterionReference,
            value: criterionReference.value == null ? [] : [ criterionReference.value ],
            schema: {
                ...templateReference.schema,
            },
        }
        : criterionReference;
}

function castYesNoText(
    criterionReference: CriterionField,
    templateReference: CriterionField,
): CriterionField {
    const value = castYesNoTextToBoolean(criterionReference.value, templateReference?.schema);
    return value === criterionReference.value ? criterionReference : { ...criterionReference, value };
}

function castReference(
    criterionReference: CriterionField,
    templateReference: CriterionField,
): CriterionField {
    return castToArray(castYesNoText(criterionReference, templateReference), templateReference);
}

function castCriterionReferenceTypeFromTemplate(
    criterion: Criterion | CriterionInput,
    template: CriterionTemplate,
): Criterion | CriterionInput {
    const syncedReferences = Object.keys(criterion.evaluator.references).reduce((acc, key) => ({
        ...acc,
        [key]: castReference(criterion.evaluator.references[key], template.evaluator.references[key]),
    }), {});
    return { ...criterion, evaluator: { ...criterion.evaluator, references: syncedReferences } };
}

/**
 * Reference keys that supersede an older reference for the same criterion. A criterion that still
 * carries the superseded key but not the superseding one hasn't been through the migration that
 * introduces the superseding key yet - the pair of keys IS the record of that. `limitSource`
 * supersedes `limitThreshold` on the Leased or Rented Equipment "Limit" criterion, where a
 * three-way radio replaced the old checkbox pair.
 */
const SUPERSEDED_REFERENCE_KEY: Record<string, string> = {
    limitSource: 'limitThreshold',
};

/**
 * True when `supersedingValue` is absent but `supersededValue` is genuinely set - i.e. the
 * criterion still relies on the superseded field and hasn't been migrated to the superseding one.
 * Backfilling the superseding key in that state would fabricate a value the customer never
 * configured, and since the evaluator prefers the superseding key once it exists, that fabricated
 * value would silently override the customer's real, superseded-key configuration on the next
 * save.
 *
 * Takes plain values rather than reference objects because the two callers store references in
 * different shapes (a `{ value, schema }` object here, a flat value in the contract-extraction
 * shape). Checking `supersededValue` itself - not just whether a reference object exists for it -
 * matters because a reference can carry a schema with no value, which is not real configuration.
 */
function isUnmigratedSupersession(supersedingValue: unknown, supersededValue: unknown): boolean {
    return supersedingValue == null && supersededValue != null;
}

/** Reads a `Criterion`/`CriterionInput`'s `{ value, schema }` references for `isUnmigratedSupersession`. */
function isCriterionReferenceUnmigrated(
    criterion: Criterion | CriterionInput,
    key: string,
): boolean {
    return isUnmigratedSupersession(
        criterion.evaluator.references[key],
        criterion.evaluator.references[SUPERSEDED_REFERENCE_KEY[key]]?.value,
    );
}

function addMissingReferencesFromTemplate(
    criterion: Criterion | CriterionInput,
    template: CriterionTemplate,
): Criterion | CriterionInput {
    const syncedReferences = Object.keys(template.evaluator.references).reduce((acc, key) => {
        // Leave the criterion without the superseding key entirely, exactly as an unmigrated row
        // already is, instead of backfilling a value the customer never configured.
        if (isCriterionReferenceUnmigrated(criterion, key)) {
            return acc;
        }
        return {
            ...acc,
            [key]: criterion.evaluator.references[key] ?? template.evaluator.references[key],
        };
    }, {});
    return { ...criterion, evaluator: { ...criterion.evaluator, references: syncedReferences } };
}

function syncSchemaFromTemplate(
    originalCriterion: Criterion | CriterionInput,
    criterion: Criterion | CriterionInput,
    template: CriterionTemplate,
): Criterion | CriterionInput {
    const syncedReferences = Object.keys(template.evaluator.references).reduce((acc, key) => {
        // Checked against `originalCriterion`, not this step's own `criterion` input: by this
        // point addMissingReferencesFromTemplate has already rebuilt `criterion`'s references from
        // the template's own key set, so an unmigrated criterion's superseded reference is no
        // longer visible on `criterion` even though it was present on the criterion this sync run
        // started from. Without checking the original, this step would hand back a schema-only
        // entry (no value, since the criterion never had one) for the superseding key.
        if (isCriterionReferenceUnmigrated(originalCriterion, key)) {
            return acc;
        }
        return {
            ...acc,
            [key]: { ...criterion.evaluator.references[key], schema: template.evaluator.references[key].schema },
        };
    }, {});
    return { ...criterion, evaluator: { ...criterion.evaluator, references: syncedReferences } };
}

function syncCriterionWithTemplate(
    criterion: Criterion | CriterionInput,
    template: CriterionTemplate,
): Criterion | CriterionInput {
    let syncCriterion = addMissingReferencesFromTemplate(criterion, template);
    syncCriterion = castCriterionReferenceTypeFromTemplate(syncCriterion, template);
    syncCriterion = syncSchemaFromTemplate(criterion, syncCriterion, template);
    return syncCriterion;
}

export function syncCriteriaWithTemplates(
    criteria: (Criterion | CriterionInput)[],
    templates: CriterionTemplate[],
): (Criterion | CriterionInput)[] {
    /* this function is for backward compatibility. if new property introduced in template but BE does not perform a
    migration on existing criteria, UI will break thus we want to sync it with template default */
    return criteria.map(
        (criterion) => {
            const template = findCriterionTemplate(criterion, templates);
            return template
                ? syncCriterionWithTemplate(criterion, template)
                : criterion;
        });
}

/** Reads an `EditableRequirementCriterion`'s flat reference values for `isUnmigratedSupersession`. */
function isEditableReferenceUnmigrated(
    criterion: EditableRequirementCriterion,
    key: string,
): boolean {
    return isUnmigratedSupersession(
        criterion.references[key],
        criterion.references[SUPERSEDED_REFERENCE_KEY[key]],
    );
}

/**
 * Syncs editable criteria references with template schemas.
 * Adds missing references from template defaults and casts values to match template schema types.
 * Used by contract extraction where criteria arrive without schema information.
 */
export function syncEditableCriteriaWithTemplates(
    criteria: EditableRequirementCriterion[],
    templates: CriterionTemplate[],
): EditableRequirementCriterion[] {
    return criteria.map((criterion) => {
        const template = templates.find(
            (t) => t.coverageType === criterion.coverageType && t.field === criterion.field,
        );
        if (!template) {
            return criterion;
        }
        const templateReferences = template.evaluator.references;
        const references = Object.keys({
            ...templateReferences,
            ...criterion.references,
        }).reduce((acc, key) => {
            // Same supersession guard as syncCriteriaWithTemplates: don't fabricate a superseding
            // key's template default while the criterion still only carries the superseded one.
            if (isEditableReferenceUnmigrated(criterion, key)) {
                return acc;
            }
            const templateRef = templateReferences[key];
            const value = criterion.references[key] ?? templateRef?.value;
            return {
                ...acc,
                [key]: templateRef ? castValueToMatchSchema(value, templateRef) : value,
            };
        }, {} as Record<string, any>);
        return { ...criterion, references };
    });
}
